// Aba "Propostas" / "Vendas" de ComercialPropostas — lista de documentos gerados.
// Extraído de ComercialPropostas.tsx durante refactor topológico.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import IconLixeira from '../../components/IconLixeira';
import { PropostaGerada } from './types';
import { getToken, fmtDate } from './utils';
import { th, td, btnAcao } from './styles';
import { StatusBadge } from './StatusBadge';
import { ViewerProposta } from './ViewerProposta';

// ─────────────────────────── DOCUMENTOS GERADOS ───────────────────────────
export function ListaGeradas({ tipo }: { tipo: 'proposta' | 'venda' }) {
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [docs, setDocs] = useState<PropostaGerada[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [visualizando, setVisualizando] = useState<string | null>(null);
  const [filtroNegocioId, setFiltroNegocioId] = useState<string>('');
  const [filtroNegocioTitulo, setFiltroNegocioTitulo] = useState<string>('');
  const [mostrarHistorico, setMostrarHistorico] = useState(false);

  useEffect(() => { carregar(); }, [tipo, mostrarHistorico]);
  // Abre direto um documento se vier intent (de NegocioDetalhe → aba Propostas)
  useEffect(() => {
    const id = sessionStorage.getItem('abrir_proposta_gerada_id');
    if (id) {
      sessionStorage.removeItem('abrir_proposta_gerada_id');
      setVisualizando(id);
    }
    const negId = sessionStorage.getItem('filtrar_propostas_negocio_id');
    const negTit = sessionStorage.getItem('filtrar_propostas_negocio_titulo');
    if (negId) {
      sessionStorage.removeItem('filtrar_propostas_negocio_id');
      sessionStorage.removeItem('filtrar_propostas_negocio_titulo');
      setFiltroNegocioId(negId);
      setFiltroNegocioTitulo(negTit || '');
    }
  }, []);
  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      params.set('tipo', tipo);
      if (!mostrarHistorico) params.set('vigente', 'true');
      const res = await axios.get(`${API_URL}/comercial/propostas-geradas?${params}`,
        { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setDocs(data);
    } finally { setLoading(false); }
  }
  async function excluir(id: string, titulo: string) {
    if (!window.confirm(`Excluir "${titulo}" definitivamente?\n\nEsta ação não pode ser desfeita.`)) return;
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/comercial/propostas-geradas/${id}`, { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) { alert('Erro: ' + (e.response?.data?.error || e.message)); }
  }
  const filtrados = docs.filter(d => {
    if (filtroNegocioId && String(d.proposta_id) !== filtroNegocioId) return false;
    if (filtroStatus !== 'todos' && d.status !== filtroStatus) return false;
    if (busca) {
      const q = busca.toLowerCase();
      const matchTitulo = d.titulo.toLowerCase().includes(q);
      const matchNegocio = (d.negocio_titulo || '').toLowerCase().includes(q);
      const matchNumero = (d.numero_proposta || '').toLowerCase().includes(q);
      if (!matchTitulo && !matchNegocio && !matchNumero) return false;
    }
    return true;
  });

  // Só na primeira carga: recarregar com um documento aberto (salvar, restaurar) não pode
  // desmontar o viewer e o histórico por cima dele.
  if (loading && !docs.length) return <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Carregando...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>
          {tipo === 'venda' ? 'Documentos de venda' : 'Propostas comerciais'}
        </h2>
        {filtroNegocioId && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 11, padding: '4px 10px', borderRadius: 4,
            background: '#E7ECFD', color: '#0930D0', fontWeight: 500,
          }}>
            Filtrado por negócio: {filtroNegocioTitulo || filtroNegocioId}
            <button onClick={() => { setFiltroNegocioId(''); setFiltroNegocioTitulo(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0930D0', fontSize: 14, padding: 0 }}>×</button>
          </span>
        )}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#555', cursor: 'pointer' }}>
          <input type="checkbox" checked={mostrarHistorico} onChange={e => setMostrarHistorico(e.target.checked)} />
          Mostrar histórico de revisões
        </label>
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
          style={{ fontSize: 12, padding: '5px 10px', borderRadius: 5, border: '0.5px solid #ddd' }}>
          <option value="todos">Todos os status</option>
          <option value="rascunho">Rascunho</option>
          <option value="enviada">Enviada</option>
          <option value="aprovada">Aprovada</option>
        </select>
        <input placeholder="🔎 Buscar por título, negócio ou nº (PRP-...)..." value={busca}
          onChange={e => setBusca(e.target.value)}
          style={{ flex: 1, maxWidth: 320, padding: '5px 10px', fontSize: 12, borderRadius: 5, border: '0.5px solid #ddd' }} />
        <span style={{ fontSize: 11, color: '#888' }}>{filtrados.length}/{docs.length}</span>
      </div>

      <div style={{ border: '0.5px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#020D7F', color: '#fff' }}>
              <th style={th}>Título</th>
              <th style={th}>Negócio</th>
              <th style={th}>Modelo</th>
              <th style={{ ...th, textAlign: 'center', width: 60 }}>Versão</th>
              <th style={{ ...th, textAlign: 'center', width: 90 }}>Status</th>
              <th style={{ ...th, textAlign: 'center', width: 100 }}>Atualizado</th>
              <th style={{ ...th, textAlign: 'center', width: 130 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.map((d, i) => (
              <tr key={d.id} style={{ borderBottom: '0.5px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#FAFBFF' }}>
                <td style={{ ...td, fontWeight: 500 }}>
                  <a onClick={() => setVisualizando(d.id)}
                    style={{ color: '#0930D0', cursor: 'pointer' }}>
                    {d.titulo}
                  </a>
                  {d.link_hash && (
                    <span style={{ fontSize: 9, marginLeft: 6, padding: '1px 5px', background: '#B07500', color: '#fff', borderRadius: 3 }}
                      title="Link público publicado">🌐</span>
                  )}
                  {d.aceite_em && (
                    <span style={{ fontSize: 9, marginLeft: 4, padding: '1px 5px', background: '#0a7f4a', color: '#fff', borderRadius: 3 }}
                      title={`Aceito por ${d.aceite_nome} em ${new Date(d.aceite_em).toLocaleString('pt-BR')}`}>✓ Aceita</span>
                  )}
                </td>
                <td style={{ ...td, color: '#666' }}>{d.negocio_titulo || '—'}</td>
                <td style={{ ...td, color: '#666' }}>{d.modelo_nome || '—'}</td>
                <td style={{ ...td, textAlign: 'center', fontSize: 11 }}>
                  v{d.versao}{(d.revisao && d.revisao > 0) ? ` · R${d.revisao}` : ''}
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <StatusBadge status={d.status} />
                </td>
                <td style={{ ...td, textAlign: 'center', fontSize: 11, color: '#666' }}>
                  {fmtDate(d.atualizado_em)}
                </td>
                <td style={{ ...td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                  <button onClick={() => setVisualizando(d.id)} style={btnAcao('#0930D0')}>Abrir</button>
                  <button onClick={() => excluir(d.id, d.titulo)} title="Excluir definitivamente" style={btnAcao('#B91626')}><IconLixeira /></button>
                </td>
              </tr>
            ))}
            {filtrados.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: '#aaa' }}>
                Nenhum documento gerado ainda. Vá em Pipeline → abra um Negócio → "📄 Gerar proposta".
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {visualizando && (
        <ViewerProposta id={visualizando}
          onClose={() => setVisualizando(null)}
          onSaved={async () => { await carregar(); }} />
      )}
    </div>
  );
}
